import type { ProFeature, TrialUsage } from '@/lib/billing/recommended-plan';

import type { PlanFeatureUsageMarker } from './plan-comparison';

import {
  getTeamOverLimitPercent,
  MIN_TEAM_OVER_LIMIT_PERCENT,
} from '@/lib/billing/recommended-plan/team-over-limit';

export const USED_IN_TRIAL_MARKER: PlanFeatureUsageMarker = {
  tone: 'positive',
  text: 'Used in trial',
  variant: 'solid',
};

export function buildTeamOverLimitMarker(
  trialUsage: TrialUsage | undefined,
): PlanFeatureUsageMarker | undefined {
  const linesReviewed = trialUsage?.linesReviewed ?? null;

  if (linesReviewed === null) {
    return undefined;
  }

  // Frame it around the user's own usage rather than asking them to compare two
  // numbers: tell them how far their projected rate exceeds the Team allowance.
  // Uses the same threshold as the recommended_by_volume funnel flag, so the two
  // agree on who is over the limit.
  const percentOver = getTeamOverLimitPercent(linesReviewed);

  // Only nudge when the projected overage is meaningful, and always show a whole
  // number rounded up so the figure reads cleanly.
  if (percentOver < MIN_TEAM_OVER_LIMIT_PERCENT) {
    return undefined;
  }

  return {
    tone: 'warning',
    text: `Your projected usage exceeds Team's limit by ${Math.ceil(percentOver)}%.`,
  };
}

/**
 * A trial's auto-fix use on Team's auto-fix line: that the trial used it, once it landed a fix.
 * Never a count or a warning: the pricing page describes auto-fix in words.
 */
export function buildAutoFixTrialMarker(
  trialUsage: TrialUsage | undefined,
): PlanFeatureUsageMarker | undefined {
  return (trialUsage?.autoFixes?.fixes ?? 0) > 0 ? USED_IN_TRIAL_MARKER : undefined;
}

export function buildUsedProFeatureSet(trialUsage: TrialUsage | undefined): Set<ProFeature> {
  return new Set(trialUsage?.featuresUsed ?? []);
}
